/**
 * How consistently anything was logged across a window.
 *
 * Adherence is the number the whole exercise depends on: a chart drawn from
 * four days out of ninety looks exactly like a chart drawn from ninety, and
 * nothing on it says which one you are reading. Coverage is what makes the
 * difference visible.
 */

import type { Entry } from '../model/entry.js'
import { dayKey, dayKeysBetween } from '../model/timestamp.js'
import { bandFor, type ConsistencyDay } from './consistency.js'
import type { ResolvedRange } from './range.js'

export interface DayCount {
  readonly dayKey: string
  readonly count: number
}

export interface Adherence {
  readonly totalDays: number
  readonly loggedDays: number
  /** 0–1. */
  readonly coverage: number
  /**
   * Only days carrying entries.
   *
   * A day with no logging is absent from this list, never present with a count
   * of zero — the same distinction the series keeps, for the same reason.
   */
  readonly perDay: readonly DayCount[]
  /** The longest run of consecutive unlogged days inside the window. */
  readonly longestGapDays: number
  /** Consecutive logged days ending on the last day of the window. */
  readonly trailingStreakDays: number
}

/**
 * Coverage across a window.
 *
 * The denominator is the window's own length in days, so it always matches what
 * the range control says. Which day an entry *lands on* is decided by its own
 * captured zone — an entry logged at 23:30 abroad belongs to the day it was
 * made, not to whatever day it was back home.
 *
 * Those two can disagree at the edges: an entry inside the window by instant
 * can carry a captured day just outside it, after a flight across the date
 * line mid-window. Such an entry appears in `perDay`, which reports what was
 * recorded, and not in `loggedDays`, which counts days of the window — so
 * coverage stays between 0 and 1 rather than exceeding it.
 */
export function adherenceFor(entries: readonly Entry[], range: ResolvedRange): Adherence {
  const counts = new Map<string, number>()
  for (const entry of entries) {
    if (entry.at.epochMs < range.fromEpochMs || entry.at.epochMs > range.toEpochMs) {
      continue
    }
    const key = dayKey(entry.at)
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }

  // The range's **own** day labels, never days re-derived from its instants in
  // some zone. Re-deriving is how the denominator ends up disagreeing with the
  // range control sitting directly above it: an "all time" window resolved in
  // an entry's zone starts at 23:00 UTC the previous evening, and counting that
  // in UTC produces one more day than the control says the window is long.
  const allDays = dayKeysBetween(range.fromDay, range.toDay)

  let longestGapDays = 0
  let runningGap = 0
  for (const key of allDays) {
    runningGap = counts.has(key) ? 0 : runningGap + 1
    longestGapDays = Math.max(longestGapDays, runningGap)
  }

  let trailingStreakDays = 0
  for (let index = allDays.length - 1; index >= 0; index -= 1) {
    const key = allDays[index]
    if (key === undefined || !counts.has(key)) {
      break
    }
    trailingStreakDays += 1
  }

  const loggedDays = allDays.filter((key) => counts.has(key)).length

  return {
    totalDays: allDays.length,
    loggedDays,
    coverage: allDays.length === 0 ? 0 : loggedDays / allDays.length,
    perDay: [...counts.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, count]) => ({ dayKey: key, count })),
    longestGapDays,
    trailingStreakDays,
  }
}

/**
 * Every day of a window, banded — the coverage heatmap (T-906).
 *
 * `perDay` above reports only days that carry entries, which is right for a
 * list and wrong for a grid: a heatmap is mostly made of the empty days, and
 * they are the whole point of looking at one. This fills them in.
 *
 * Banded with `bandFor`, the same function Home's strip uses, so a colour means
 * the same thing on both screens. Every count it encodes is also stated in
 * words beside it — the grid is decoration over a fact, never the only place
 * the fact appears.
 *
 * Across the **resolved range**, matching the chart beside it. An earlier
 * design capped the grid at thirty cells counting back from today, which
 * described a different period from a chart titled "Last 90 days" sitting next
 * to it.
 */
export function coverageDays(
  entries: readonly Entry[],
  range: ResolvedRange,
): readonly ConsistencyDay[] {
  const counts = new Map<string, number>()
  for (const day of adherenceFor(entries, range).perDay) {
    counts.set(day.dayKey, day.count)
  }
  return dayKeysBetween(range.fromDay, range.toDay).map((dayKey) => {
    const count = counts.get(dayKey) ?? 0
    return { dayKey, count, band: bandFor(count) }
  })
}
